import React, { useState } from 'react';
import { 
  Webhook, Trash2, Edit, Play, History, 
  Eye, EyeOff, Copy, Loader2 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

export const WebhookCard = ({ webhook, onEdit, onDelete, onTest, onViewHistory }) => {
  const [showSecret, setShowSecret] = useState(false);
  const [testing, setTesting] = useState(false);
  
  const handleTest = async () => {
    setTesting(true);
    await onTest(webhook.id);
    setTesting(false);
  };
  
  const copySecret = () => {
    navigator.clipboard.writeText(webhook.secret || '');
    toast.success('Secret copiado!');
  };
  
  const successRate = webhook.total_calls > 0 
    ? ((webhook.successful_calls / webhook.total_calls) * 100).toFixed(0)
    : 100;
  
  return (
    <Card className={`glass-card ${!webhook.is_active ? 'opacity-60' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Webhook className="w-5 h-5 text-primary" />
              {webhook.name}
            </CardTitle>
            <CardDescription className="mt-1 truncate max-w-[250px]">
              {webhook.url}
            </CardDescription>
          </div>
          <Badge variant={webhook.is_active ? 'default' : 'secondary'}>
            {webhook.is_active ? 'Ativo' : 'Inativo'}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Events */}
        <div className="mb-4">
          <p className="text-xs text-muted-foreground mb-2">Eventos ({webhook.events?.length || 0})</p>
          <div className="flex flex-wrap gap-1">
            {webhook.events?.slice(0, 3).map(event => (
              <Badge key={event} variant="outline" className="text-xs">
                {event.split('.')[1]}
              </Badge>
            ))}
            {webhook.events?.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{webhook.events.length - 3}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Secret */}
        {webhook.secret && (
          <div className="mb-4">
            <p className="text-xs text-muted-foreground mb-1">Secret</p>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">
                {showSecret ? webhook.secret : '••••••••••••••••••••'}
              </code>
              <Button size="icon" variant="ghost" onClick={() => setShowSecret(!showSecret)}>
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
              <Button size="icon" variant="ghost" onClick={copySecret}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
          <div>
            <p className="text-lg font-bold">{webhook.total_calls}</p>
            <p className="text-xs text-muted-foreground">Chamadas</p>
          </div>
          <div>
            <p className="text-lg font-bold text-green-400">{successRate}%</p>
            <p className="text-xs text-muted-foreground">Sucesso</p>
          </div>
          <div>
            <p className="text-lg font-bold text-red-400">{webhook.failed_calls}</p>
            <p className="text-xs text-muted-foreground">Falhas</p>
          </div>
        </div>
        
        {/* Last triggered */}
        {webhook.last_triggered_at && (
          <p className="text-xs text-muted-foreground mb-4">
            Último: {new Date(webhook.last_triggered_at).toLocaleString('pt-BR')}
          </p>
        )}
        
        {/* Actions */}
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={handleTest}
            disabled={testing}
            data-testid="webhook-test-btn"
          >
            {testing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onViewHistory(webhook.id)}>
            <History className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => onEdit(webhook)}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(webhook.id)}>
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default WebhookCard;
